import type { HTMLAttributes, ReactNode, JSX } from 'react'

import { clsx } from 'clsx'

import { Text } from '@buffer-mono/popcorn'

import styles from './EngagementReplyFormItem.module.css'

type EngagementReplyFormItemElement = 'div' | 'section'

interface EngagementReplyFormItemProps extends Omit<
  HTMLAttributes<HTMLElement>,
  'as'
> {
  /**
   * The HTML element type to render. Defaults to 'div'.
   */
  as?: EngagementReplyFormItemElement
  /**
   * Node rendered in the left column – the avatar.
   */
  avatar: ReactNode
  /**
   * Node rendered above the main content - suggestions.
   */
  suggestions?: ReactNode
  /**
   * Children rendered in the right (content) column.
   */
  children: ReactNode
  /**
   * Error message to display.
   */
  error?: string
  /**
   * `id` stamped on the error text so the reply textarea's
   * `aria-describedby` resolves to it for screen readers.
   */
  errorId?: string
}

const ErrorMessage = ({
  errorMessage,
  id,
}: {
  errorMessage?: string
  id?: string
}): JSX.Element => (
  <Text id={id} size="sm" color="critical" className={styles.error}>
    {errorMessage}
  </Text>
)

/**
 * Layout primitive for an engagement reply form (comment or mention): an
 * avatar column with a continuous thread connector, an optional suggestions
 * row, an optional error message, and the form content. Prop-driven — the
 * per-feature reply forms feed it.
 */
export const EngagementReplyFormItem = ({
  as: Component = 'div',
  avatar,
  suggestions,
  children,
  className,
  error,
  errorId,
  ...props
}: EngagementReplyFormItemProps): JSX.Element => {
  return (
    <Component
      data-testid="engagement-reply-form-item"
      {...props}
      className={clsx(styles.container, className)}
      data-has-error={!!error}
    >
      {error && <ErrorMessage errorMessage={error} id={errorId} />}
      {suggestions && <div className={styles.suggestions}>{suggestions}</div>}
      {/* Connector line in the (otherwise empty) avatar column, spanning the
       * error + header rows above the avatar. The grid sizes it automatically,
       * so the thread line stays continuous however tall the error grows —
       * the avatar's own fixed-height connector can't reach across it. */}
      <div className={styles.connector} aria-hidden />
      <div className={styles.mainContent}>
        <aside className={styles.avatar}>{avatar}</aside>
        <div className={styles.content}>{children}</div>
      </div>
    </Component>
  )
}

EngagementReplyFormItem.displayName = 'EngagementReplyFormItem'
